import { computed, readonly, ref } from 'vue';
import type { AuthUser } from '@gtexai/shared';
import { api } from './api';

const user = ref<AuthUser | null>(null);
const remaining = ref<number | null>(null);
/** Null until the first restore attempt finishes, so guards can wait. */
const ready = ref(false);

let restoring: Promise<void> | null = null;

/**
 * Turns the httpOnly refresh cookie back into a session on page load.
 *
 * Deduplicated: the router guard and the app shell both want this on first
 * paint, and two concurrent /auth/refresh calls would rotate the token twice —
 * the second rotation invalidating the first and tripping reuse detection.
 */
export function restore(): Promise<void> {
  restoring ??= api
    .restore()
    .then((session) => {
      user.value = session?.user ?? null;
    })
    .finally(() => {
      ready.value = true;
      restoring = null;
    });
  return restoring;
}

export async function refreshBalance(): Promise<void> {
  if (!user.value) return;
  remaining.value = (await api.remaining()).generations;
}

export async function signIn(email: string, password: string): Promise<void> {
  const session = await api.login(email, password);
  user.value = session.user;
  await refreshBalance();
}

/**
 * Signs in from a link rather than a password, and returns where to go.
 *
 * Kept beside `signIn` so both leave the module in the same state — a session
 * begun by link is a session in every other respect, and nothing downstream
 * should be able to tell the difference.
 */
export async function signInWithLink(token: string): Promise<string> {
  const result = await api.linkLogin(token);
  user.value = result.user;
  await refreshBalance();
  return result.landing;
}

export async function signOut(): Promise<void> {
  await api.logout();
  user.value = null;
  remaining.value = null;
}

export const session = {
  user: readonly(user),
  remaining: readonly(remaining),
  ready: readonly(ready),
  isAuthenticated: computed(() => user.value !== null),
};
